import { isEmpty } from "ramda";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Typography } from "@/components/atoms";
import { DataTable, EmptyState } from "@/components/molecules";
import { FixedPricingLocation } from "@/redux/slices/trip/types";
import { usePricingProfileContext } from "../PricingProfile/PricingProfileContext";
import { getPaginationLabel } from "../helpers";
import { DeleteZonePricingModal } from "./DeleteZonePricingModal";
import { useZoneRatesContext } from "./ZoneRatesContext";
import { generateColumns, generateData } from "./helpers";
import { ZoneRatesAction } from "./types";

export const ZoneRatesPanel = () => {
  const navigate = useNavigate();
  const { zones, totalZones } = useZoneRatesContext();
  const { pricingProfile } = usePricingProfileContext();
  const [action, setAction] = useState<ZoneRatesAction>(undefined);
  const [selectedZone, setSelectedZone] = useState<FixedPricingLocation | undefined>(undefined);

  const handleManage = (zone: FixedPricingLocation) => navigate(zone.id);

  const handleDelete = (zone: FixedPricingLocation) => {
    setSelectedZone(zone);
    setAction("deleteZonePricing");
  };

  const data = useMemo(
    () => generateData(zones, handleManage, handleDelete),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [zones]
  );

  const columns = useMemo(
    () => generateColumns(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <div className="flex flex-col gap-y-4">
      <div className="flex flex-col justify-between gap-4 xl:flex-row">
        <div className="flex flex-col gap-1">
          <Typography variant="h2">{`${pricingProfile ? `${pricingProfile.name}: ` : ""}Zone Rates`}</Typography>
          <Typography variant="paragraph" className="text-neutral-dark-gray">
            Set special prices for trips that start in specific areas. You can add multiple zone rates as needed.
          </Typography>
        </div>
        {!isEmpty(data) && (
          <div>
            <Button variant="primary" size="sm" startIcon="Add" onClick={() => navigate("add")}>
              Add Zone Pricing
            </Button>
          </div>
        )}
      </div>
      {isEmpty(data) ? (
        <div className="rounded-lg border border-neutral-mid-gray bg-neutral-surface-gray">
          <EmptyState
            title="Nothing here… yet"
            description="You haven't added anything to this section. Get started by clicking the button below."
          >
            <Button variant="primary" size="md" startIcon="Add" onClick={() => navigate("add")}>
              Add Zone Pricing
            </Button>
          </EmptyState>
        </div>
      ) : (
        <div>
          <DataTable columns={columns} data={data} className="w-full overflow-x-auto rounded-b-none bg-white [&_table]:table-auto" />
          <div className="flex w-full items-center justify-between rounded-b-lg bg-neutral-surface-gray p-2 ring-1 ring-neutral-mid-gray">
            <Typography variant="small-regular" className="text-neutral-silver-gray">
              {getPaginationLabel(1, data.length, totalZones)}
            </Typography>
          </div>
        </div>
      )}
      <DeleteZonePricingModal
        open={action === "deleteZonePricing"}
        onClose={() => {
          setAction(undefined);
          setSelectedZone(undefined);
        }}
        selectedZone={selectedZone}
        pricingProfile={pricingProfile}
      />
    </div>
  );
};
